import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input } from '../components/ui';
import { useToast } from '../components/ui/ToastProvider';
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadProductPhotos,
  getProductPhotos,
  deleteProductPhoto, // Import the delete function
  Product as ApiProduct
} from '../services/productService';
import {
  getCategories,
  Category,
  Subcategory
} from '../services/categoryService';
import '../styles/pages.css';

// Helper function to normalize language code
const normalizeLang = (lang: string): 'en' | 'ar' => {
  return lang.startsWith('ar') ? 'ar' : 'en';
};

interface Product {
  id: string;
  name: {
    en: string;
    ar: string;
  };
  description: {
    en: string;
    ar: string;
  };
  price: number;
  category: string;
  subcategory?: string;
  // stock: number;
  images: string[];
  photos?: { id: string; url: string }[]; // Add photos array with IDs
  status: 'active' | 'inactive';
  createdAt: string;
}

// Enhanced Product Card Component with image cycling
interface ProductCardProps {
  product: Product;
  onEdit: (product: Product) => void;
  onToggleStatus: (id: string) => void;
  onDelete: (id: string) => void;
  onClick: (product: Product) => void;
  t: any;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onEdit, onToggleStatus, onDelete, onClick, t }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { i18n } = useTranslation();
  const currentLang = normalizeLang(i18n.language);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  return (
    <div className="product-card enhanced" onClick={() => onClick(product)}>
      <div className="product-image-container">
        <img
          src={product.images[currentImageIndex] || '/images/placeholder-product.png'}
          alt={product.name[currentLang]}
          className="product-image"
          onError={(e) => {
            e.currentTarget.src = '/images/placeholder-product.png';
          }}
        />

        {/* Image Navigation Controls */}
        {product.images.length > 1 && (
          <>
            <button className="image-nav prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}>‹</button>
            <button className="image-nav next" onClick={(e) => { e.stopPropagation(); nextImage(); }}>›</button>

            {/* Image Indicators */}
            <div className="image-indicators">
              {product.images.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); goToImage(index); }}
                />
              ))}
            </div>
          </>
        )}

        {/* Status Badge */}
        <div className={`status-badge ${product.status}`}>
          {t(`products.status.${product.status}`)}
        </div>
      </div>

      <div className="product-info">
        <h3 className="product-name">
          {product.name[currentLang]}
        </h3>
        <p className="product-description">
          {product.description[currentLang]}
        </p>
        <div className="product-details">
          <span className="price">{product.price} {t('common.currency')}</span>
          {/* <span className={`stock ${product.stock === 0 ? 'out-of-stock' : ''}`}>
            {product.stock === 0
              ? t('products.outOfStock')
              : `${product.stock} ${t('products.inStock')}`
            }
          </span> */}
        </div>
        <div className="product-category">
          <span className="category-tag">{product.category}</span>
          {product.subcategory && (
            <span className="subcategory-tag">{product.subcategory}</span>
          )}
        </div>
      </div>

      {/* Control Buttons at Bottom */}
      <div className="product-actions" onClick={(e) => e.stopPropagation()}>
        <button className="control-btn edit" onClick={() => onEdit(product)}>
          <span>✏️</span>
          {t('products.edit')}
        </button>
        <button
          className={`control-btn toggle ${product.status === 'active' ? 'deactivate' : 'activate'}`}
          onClick={() => onToggleStatus(product.id)}
        >
          <span>{product.status === 'active' ? '⏸️' : '▶️'}</span>
          {product.status === 'active' ? t('products.deactivate') : t('products.activate')}
        </button>
        <button className="control-btn delete" onClick={() => onDelete(product.id)}>
          <span>🗑️</span>
          {t('products.delete')}
        </button>
      </div>
    </div>
  );
};

// Product Details Modal Component
interface ProductDetailsModalProps {
  product: Product;
  onClose: () => void;
  onEdit: (product: Product) => void;
  t: any;
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({ product, onClose, onEdit, t }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { i18n } = useTranslation();
  const currentLang = normalizeLang(i18n.language);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content product-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{product.name[currentLang]}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body product-details-body">
          {/* Image Gallery Section */}
          <div className="product-details-images">
            <div className="main-image-container">
              <img
                src={product.images[currentImageIndex] || '/images/placeholder-product.png'}
                alt={product.name[currentLang]}
                className="main-product-image"
                onError={(e) => {
                  e.currentTarget.src = '/images/placeholder-product.png';
                }}
              />

              {/* Image Navigation */}
              {product.images.length > 1 && (
                <>
                  <button className="image-nav prev" onClick={prevImage}>‹</button>
                  <button className="image-nav next" onClick={nextImage}>›</button>
                </>
              )}

              {/* Status Badge */}
              <div className={`status-badge ${product.status}`}>
                {t(`products.status.${product.status}`)}
              </div>
            </div>

            {/* Thumbnail Gallery */}
            {product.images.length > 1 && (
              <div className="thumbnail-gallery">
                {product.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${product.name[currentLang]} ${index + 1}`}
                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => goToImage(index)}
                    onError={(e) => {
                      e.currentTarget.src = '/images/placeholder-product.png';
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Product Information Section */}
          <div className="product-details-info">
            <div className="product-header">
              <h3 className="product-title">
                {product.name[currentLang]}
              </h3>
              <div className="product-price-large">
                {product.price} {t('common.currency')}
              </div>
            </div>

            <div className="product-description-full">
              <h4>{t('products.description')}</h4>
              <div className="description-content">
                <p>{product.description[currentLang]}</p>
              </div>
            </div>

            <div className="product-meta">
              <div className="meta-item">
                <span className="meta-label">{t('products.category')}:</span>
                <span className="meta-value">{product.category}</span>
              </div>
              {product.subcategory && (
                <div className="meta-item">
                  <span className="meta-label">{t('products.subcategory')}:</span>
                  <span className="meta-value">{product.subcategory}</span>
                </div>
              )}
              <div className="meta-item">
                <span className="meta-label">{t('products.stock')}:</span>
                {/* <span className={`meta-value ${product.stock === 0 ? 'out-of-stock' : ''}`}>
                  {product.stock === 0
                    ? t('products.outOfStock')
                    : `${product.stock} ${t('products.inStock')}`
                  }
                </span> */}
              </div>
              <div className="meta-item">
                <span className="meta-label">{t('common.status')}:</span>
                <span className={`meta-value status-${product.status}`}>
                  {t(`products.status.${product.status}`)}
                </span>
              </div>
              <div className="meta-item">
                <span className="meta-label">{t('products.created')}:</span>
                <span className="meta-value">{product.createdAt}</span>
              </div>
            </div>

            <div className="product-details-actions">
              <Button variant="primary" onClick={() => onEdit(product)}>
                {t('products.editProduct')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Products: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: { en: '', ar: '' },
    description: { en: '', ar: '' },
    price: 0,
    category: '',
    subcategory: '',
    // stock: 0,
    images: [] as string[],
    photos: [] as { id: string; url: string }[],
    status: 'active' as 'active' | 'inactive'
  });

  // Image management states
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [productPhotos, setProductPhotos] = useState<Array<{ id: string; url: string }>>([]);

  // Category dropdown states
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
  const [subcategorySearch, setSubcategorySearch] = useState('');
  const [isDeleteHasHover, setIsDeleteHasHover] = useState(false);

  // Categories and subcategories from API
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategoriesMap, setSubcategoriesMap] = useState<Map<string, Subcategory[]>>(new Map());

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        setShowCategoryDropdown(false);
        setShowSubcategoryDropdown(false);
      }
    };

    if (showCategoryDropdown || showSubcategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCategoryDropdown, showSubcategoryDropdown]);

  // Load products and categories from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load products and categories in parallel
        const [productsData, categoriesData] = await Promise.all([
          getProducts(),
          getCategories()
        ]);

        // Convert API products to UI format (isActive -> status)
        const uiProducts: Product[] = productsData.map(p => ({
          id: p.id,
          name: p.name,
          description: p.description,
          price: p.price,
          category: p.category,
          subcategory: p.subcategory,
          images: p.images,
          photos: p.photos, // Pass the photos array
          status: p.isActive ? 'active' : 'inactive',
          createdAt: p.createdAt
        }));

        setProducts(uiProducts);
        setCategories(categoriesData);

        // Build subcategories map by category name
        const currentLang = normalizeLang(i18n.language);
        const subMap = new Map<string, Subcategory[]>();
        categoriesData.forEach(cat => {
          const categoryName = cat.name[currentLang];
          subMap.set(categoryName, cat.subcategories);
        });
        setSubcategoriesMap(subMap);
      } catch (error: any) {
        console.error('Failed to load data:', error);
        addToast({
          title: t('notifications.error'),
          message: error?.message || t('notifications.operationFailed'),
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [addToast, i18n.language]);

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesCategory;
  });

  // Get unique categories from products
  const productCategories = ['all', ...Array.from(new Set(products.map(p => p.category)))];

  const resetForm = () => {
    setProductForm({
      name: { en: '', ar: '' },
      description: { en: '', ar: '' },
      price: 0,
      category: '',
      subcategory: '',
      // stock: 0,
      images: [],
      photos: [],
      status: 'active'
    });
    setSelectedFiles([]);
    setImagePreviewUrls([]);
    setProductPhotos([]);
    setCategorySearch('');
    setSubcategorySearch('');
    setShowCategoryDropdown(false);
    setShowSubcategoryDropdown(false);
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    resetForm();
    setShowAddModal(true);
  };

  const handleEditProduct = async (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      subcategory: product.subcategory || '',
      images: product.images,
      photos: product.photos ?? [], // Add photos to the form state
      status: product.status
    });
    // Set existing images as preview URLs
    setImagePreviewUrls(product.images || []);
    setSelectedFiles([]);
    setCategorySearch(product.category);
    setSubcategorySearch(product.subcategory || '');
    
    // Fetch detailed photo information with IDs
    try {
      const photos = await getProductPhotos(product.id);
      setProductPhotos(photos.map(p => ({ id: p.id, url: p.photoUrl })));
    } catch (error) {
      console.error('Failed to fetch product photos:', error);
      setProductPhotos([]);
    }
    
    setShowAddModal(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name.en || !productForm.name.ar || !productForm.subcategory || productForm.price <= 0) {
      addToast({ title: t('notifications.error'), message: t('forms.required'), type: 'error' });
      return;
    }

    // Images are optional - can be uploaded after product creation

    // Find subcategory ID from name using normalized language
    const currentLang = normalizeLang(i18n.language);
    const selectedSubcategory = categories
      .flatMap(cat => cat.subcategories)
      .find(sub => {
        const subNameEn = sub.name.en;
        const subNameAr = sub.name.ar;
        return subNameEn === productForm.subcategory || subNameAr === productForm.subcategory;
      });

    if (!selectedSubcategory) {
      console.error('Subcategory not found:', productForm.subcategory);
      console.log('Available subcategories:', categories.flatMap(cat => cat.subcategories).map(s => ({ en: s.name.en, ar: s.name.ar })));
      addToast({ title: t('notifications.error'), message: t('forms.selectOption'), type: 'error' });
      return;
    }

    console.log('Selected subcategory:', selectedSubcategory);

    try {
      if (editingProduct) {
        // Update existing product
        const updateData: any = {
          name: productForm.name,
          description: productForm.description,
          price: productForm.price,
          isActive: productForm.status === 'active',
          subcategoryId: Number(selectedSubcategory.id)
        };

        console.log('Updating product with data:', updateData);

        const updatedProduct = await updateProduct(editingProduct.id, updateData);

        // Upload new photos if any
        if (selectedFiles.length > 0) {
          await uploadProductPhotos(updatedProduct.id, selectedFiles);
        }

        // Refresh products list
        const productsData = await getProducts();
        const uiProducts: Product[] = productsData.map(p => ({
          id: p.id,
          name: p.name,
          description: p.description,
          price: p.price,
          category: p.category,
          subcategory: p.subcategory,
          images: p.images,
          photos: p.photos, // Pass the photos array
          status: p.isActive ? 'active' : 'inactive',
          createdAt: p.createdAt
        }));
        setProducts(uiProducts);

        addToast({ title: t('notifications.success'), message: t('products.productHasBeenUpdated'), type: 'success' });
      } else {
        // Create new product
        const createData = {
          name: productForm.name,
          description: productForm.description,
          price: productForm.price,
          subcategoryId: Number(selectedSubcategory.id),
          isActive: productForm.status === 'active'
        };

        console.log('Creating product with data:', createData);

        const newProduct = await createProduct(createData);

        console.log('Product created:', newProduct);

        // Upload photos if any
        if (selectedFiles.length > 0) {
          console.log('Uploading photos:', selectedFiles.length);
          await uploadProductPhotos(newProduct.id, selectedFiles);
        }

        // Refresh products list
        const productsData = await getProducts();
        const uiProducts: Product[] = productsData.map(p => ({
          id: p.id,
          name: p.name,
          description: p.description,
          price: p.price,
          category: p.category,
          subcategory: p.subcategory,
          images: p.images,
          photos: p.photos, // Pass the photos array
          status: p.isActive ? 'active' : 'inactive',
          createdAt: p.createdAt
        }));
        setProducts(uiProducts);

        addToast({ title: t('notifications.success'), message: t('products.newProductAdded'), type: 'success' });
      }

      // Clean up blob URLs
      imagePreviewUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });

      setShowAddModal(false);
      resetForm();
      setEditingProduct(null);
    } catch (error: any) {
      console.error('Failed to save product:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response,
        data: error?.response?.data
      });
      addToast({
        title: t('notifications.error'),
        message: error?.message || t('notifications.operationFailed'),
        type: 'error'
      });
    }
  };

  const handleDeleteProduct = (id: string) => {
    setProductToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;

    try {
      await deleteProduct(productToDelete);
      
      // Refresh products list
      const productsData = await getProducts();
      const uiProducts: Product[] = productsData.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description,
        price: p.price,
        category: p.category,
        subcategory: p.subcategory,
        images: p.images,
        photos: p.photos, // Pass the photos array
        status: p.isActive ? 'active' : 'inactive',
        createdAt: p.createdAt
      }));
      setProducts(uiProducts);

      addToast({ title: t('notifications.success'), message: t('products.productHasBeenDeleted'), type: 'success' });
      setShowDeleteModal(false);
      setProductToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete product:', error);
      addToast({
        title: t('notifications.error'),
        message: error?.message || t('notifications.operationFailed'),
        type: 'error'
      });
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedProduct(null);
  };

  const handleToggleStatus = async (id: string) => {
    try {
      const product = products.find(p => p.id === id);
      if (!product) return;

      const newStatus = product.status === 'active' ? 'inactive' : 'active';
      
      await updateProduct(id, {
        isActive: newStatus === 'active'
      });

      // Refresh products list
      const productsData = await getProducts();
      const uiProducts: Product[] = productsData.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description,
        price: p.price,
        category: p.category,
        subcategory: p.subcategory,
        images: p.images,
        photos: p.photos, // Pass the photos array
        status: p.isActive ? 'active' : 'inactive',
        createdAt: p.createdAt
      }));
      setProducts(uiProducts);

      addToast({ title: t('notifications.success'), message: t('products.statusHasBeenUpdated'), type: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      addToast({
        title: t('notifications.error'),
        message: error?.message || t('notifications.operationFailed'),
        type: 'error'
      });
    }
  };

  // Category dropdown helpers
  const currentLang = i18n.language.startsWith('ar') ? 'ar' : 'en'; // Normalize language code
  
  const filteredCategories = categories.filter(cat => {
    const catName = cat.name[currentLang];
    return catName && catName.toLowerCase().includes(categorySearch.toLowerCase());
  });

  const filteredSubcategories = productForm.category && subcategoriesMap.has(productForm.category)
    ? (subcategoriesMap.get(productForm.category) || []).filter(sub => {
        const subName = sub.name[currentLang];
        return subName && subName.toLowerCase().includes(subcategorySearch.toLowerCase());
      })
    : [];

  const handleCategorySelect = (categoryName: string) => {
    setProductForm(prev => ({ ...prev, category: categoryName, subcategory: '' }));
    setCategorySearch(categoryName);
    setShowCategoryDropdown(false);
    setSubcategorySearch('');
  };

  const handleSubcategorySelect = (subcategoryName: string) => {
    setProductForm(prev => ({ ...prev, subcategory: subcategoryName }));
    setSubcategorySearch(subcategoryName);
    setShowSubcategoryDropdown(false);
  };

  // Image handling functions
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const validFiles = fileArray.filter(file => {
        const isValidType = file.type.startsWith('image/');
        const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
        return isValidType && isValidSize;
      });

      if (validFiles.length !== fileArray.length) {
        addToast({
          title: t('notifications.warning'),
          message: t('forms.fileTooLarge'),
          type: 'warning'
        });
      }

      const newFiles = [...selectedFiles, ...validFiles];
      setSelectedFiles(newFiles);

      // Create preview URLs for new files
      const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
      setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);

      // Update form with combined images (existing + new file URLs)
      const combinedImages = [...productForm.images, ...newPreviewUrls];
      setProductForm(prev => ({ ...prev, images: combinedImages }));
    }
  };

  const handleRemoveImage = (index: number) => {
    const newPreviewUrls = [...imagePreviewUrls];
    const removedUrl = newPreviewUrls[index];

    // If it's a blob URL, revoke it to free memory
    if (removedUrl.startsWith('blob:')) {
      URL.revokeObjectURL(removedUrl);
      // Also remove from selectedFiles
      const fileIndex = selectedFiles.findIndex((_, i) =>
        URL.createObjectURL(selectedFiles[i]) === removedUrl
      );
      if (fileIndex !== -1) {
        const newFiles = [...selectedFiles];
        newFiles.splice(fileIndex, 1);
        setSelectedFiles(newFiles);
      }
    }

    newPreviewUrls.splice(index, 1);
    setImagePreviewUrls(newPreviewUrls);
    setProductForm(prev => ({ ...prev, images: newPreviewUrls }));
  };

  const handleDeleteImage = async (photoId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent modal from closing
    
    console.log('Attempting to delete photo with ID:', photoId);
    
    if (window.confirm(t('products.confirmDeletePhoto'))) {
      try {
        console.log('Calling deleteProductPhoto API...');
        await deleteProductPhoto(photoId);
        console.log('Photo deleted successfully from server');

        // Refresh the products list to show the change
        const productsData = await getProducts();
        const uiProducts: Product[] = productsData.map(p => ({
          id: p.id,
          name: p.name,
          description: p.description,
          price: p.price,
          category: p.category,
          subcategory: p.subcategory,
          images: p.images,
          photos: p.photos,
          status: p.isActive ? 'active' : 'inactive',
          createdAt: p.createdAt
        }));
        setProducts(uiProducts);
        
        // Also update the form state if we are editing a product
        if (editingProduct) {
            const updatedProduct = uiProducts.find(p => p.id === editingProduct.id);
            if (updatedProduct) {
              setProductForm(prev => ({
                ...prev,
                images: updatedProduct.images,
                photos: updatedProduct.photos ?? [],
              }));
              setImagePreviewUrls(updatedProduct.images);
            }
        }

        addToast({ title: t('notifications.success'), message: t('products.photoHasBeenDeleted'), type: 'success' });
      } catch (error: any) {
        console.error('Failed to delete photo:', error);
        addToast({
          title: t('notifications.error'),
          message: error?.message || t('notifications.operationFailed'),
          type: 'error'
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">{t('navigation.products')}</h1>
        </div>
        <div className="page-content">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">{t('navigation.products')}</h1>
        <Button
          variant="primary"
          onClick={handleAddProduct}
          className="add-btn"
        >
          {t('products.addProduct')}
        </Button>
      </div>

      <div className="page-content">
        <Card className="filters-card">
          <div className="filters-row">
            <div className="filter-group">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-select"
              >
                {productCategories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? t('products.allCategories') : category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        <div className="products-grid">
          {filteredProducts.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              onEdit={handleEditProduct}
              onToggleStatus={handleToggleStatus}
              onDelete={handleDeleteProduct}
              onClick={handleProductClick}
              t={t}
            />
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="empty-state">
            <p>{t('products.noProducts')}</p>
          </div>
        )}
      </div>

      {/* Add/Edit Product Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => {
          // Clean up blob URLs when closing modal
          imagePreviewUrls.forEach(url => {
            if (url.startsWith('blob:')) {
              URL.revokeObjectURL(url);
            }
          });
          setShowAddModal(false);
          resetForm();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? t('products.editProduct') : t('products.addNewProduct')}</h2>
              <button
                className="modal-close"
                onClick={() => {
                  // Clean up blob URLs when closing modal
                  imagePreviewUrls.forEach(url => {
                    if (url.startsWith('blob:')) {
                      URL.revokeObjectURL(url);
                    }
                  });
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('products.productNameEn')}</label>
                  <Input
                    value={productForm.name.en}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      name: { ...prev.name, en: e.target.value }
                    }))}
                    placeholder={t('products.enterProductNameEn')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('products.productNameAr')}</label>
                  <Input
                    value={productForm.name.ar}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      name: { ...prev.name, ar: e.target.value }
                    }))}
                    placeholder={t('products.enterProductNameAr')}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('products.descriptionEn')}</label>
                  <textarea
                    value={productForm.description.en}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      description: { ...prev.description, en: e.target.value }
                    }))}
                    placeholder={t('products.enterDescriptionEn')}
                    className="form-textarea"
                  />
                </div>
                <div className="form-group">
                  <label>{t('products.descriptionAr')}</label>
                  <textarea
                    value={productForm.description.ar}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      description: { ...prev.description, ar: e.target.value }
                    }))}
                    placeholder={t('products.enterDescriptionAr')}
                    className="form-textarea"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('products.price')}</label>
                  <Input
                    type="number"
                    value={productForm.price}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      price: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="0.00"
                  />
                </div>
                {/* <div className="form-group">
                  <label>Stock</label>
                  <Input
                    type="number"
                    value={productForm.stock}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      stock: parseInt(e.target.value) || 0
                    }))}
                    placeholder="0"
                  />
                </div> */}

                <div className="form-group">
                  <label>{t('common.status')}</label>
                  <select
                    value={productForm.status}
                    onChange={(e) => setProductForm(prev => ({
                      ...prev,
                      status: e.target.value as 'active' | 'inactive'
                    }))}
                    className="form-select"
                  >
                    <option value="active">{t('products.status.active')}</option>
                    <option value="inactive">{t('products.status.inactive')}</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('products.category')}</label>
                  <div className="dropdown-container">
                    <Input
                      value={categorySearch}
                      onChange={(e) => {
                        setCategorySearch(e.target.value);
                        setShowCategoryDropdown(true);
                      }}
                      onFocus={() => setShowCategoryDropdown(true)}
                      placeholder={t('products.searchAndSelectCategory')}
                      className="dropdown-input"
                    />
                    {showCategoryDropdown && (
                      <div className="dropdown-menu">
                        {filteredCategories.length > 0 ? (
                          filteredCategories.map(category => {
                            const catName = category.name[currentLang];
                            return (
                              <div
                                key={category.id}
                                className="dropdown-item"
                                onClick={() => handleCategorySelect(catName)}
                              >
                                {catName}
                              </div>
                            );
                          })
                        ) : (
                          <div className="dropdown-item dropdown-item--disabled">
                            {t('products.noCategories')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {productForm.category && (
                  <div className="form-group">
                    <label>{t('products.subcategory')}</label>
                    <div className="dropdown-container">
                      <Input
                        value={subcategorySearch}
                        onChange={(e) => {
                          setSubcategorySearch(e.target.value);
                          setShowSubcategoryDropdown(true);
                        }}
                        onFocus={() => setShowSubcategoryDropdown(true)}
                        placeholder={t('products.searchAndSelectSubcategory')}
                        className="dropdown-input"
                      />
                      {showSubcategoryDropdown && (
                        <div className="dropdown-menu">
                          {filteredSubcategories.length > 0 ? (
                            filteredSubcategories.map(subcategory => {
                              const subName = subcategory.name[currentLang];
                              return (
                                <div
                                  key={subcategory.id}
                                  className="dropdown-item"
                                  onClick={() => handleSubcategorySelect(subName)}
                                >
                                  {subName}
                                </div>
                              );
                            })
                          ) : (
                            <div className="dropdown-item dropdown-item--disabled">
                              {t('products.noSubcategories')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      {/* Empty space for alignment */}
                    </div>
                  </div>
                )}
              </div>



              <div className="form-group">
                <label>{t('products.productImages')} ({t('products.atLeastOneImageRequired')})</label>
                <div className="image-upload-container">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    className="image-input"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="image-upload-button">
                    <span>📷</span>
                    {t('products.selectImages')}
                  </label>
                  <span className="image-upload-hint">
                    {t('products.imageUploadHint')}
                  </span>
                </div>

                {imagePreviewUrls.length > 0 && (
                  <div className="image-preview-gallery">
                    {imagePreviewUrls.map((url, index) => {
                      // Find the corresponding photo ID from the fetched productPhotos array
                      const photoWithId = productPhotos.find(p => p.url === url);
                      const photoId = photoWithId?.id || null;
                      const isExistingPhoto = !!photoId && !url.startsWith('blob:');

                      return (
                        <div key={photoId || index} className="image-preview-item">
                          <img src={url} alt={`Preview ${index + 1}`} />
                          {isExistingPhoto ? (
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={(e) => handleDeleteImage(photoId!, e)}
                              title="Delete this photo from server"
                            >
                              ×
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={() => handleRemoveImage(index)}
                              title="Remove this preview"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <Button
                variant="secondary"
                onClick={() => {
                  // Clean up blob URLs when canceling
                  imagePreviewUrls.forEach(url => {
                    if (url.startsWith('blob:')) {
                      URL.revokeObjectURL(url);
                    }
                  });
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveProduct}
              >
                {editingProduct ? t('products.updateProduct') : t('products.addProduct')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('products.confirmDeleteTitle')}</h2>
              <button className="modal-close" onClick={cancelDelete}>×</button>
            </div>
            <div className="modal-body">
              <div className="delete-confirmation">
                <div className="delete-icon">🗑️</div>
                <h3>{t('products.deleteConfirm')}</h3>
                <p>{t('products.deleteMessage')}</p>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={cancelDelete}>
                {t('common.cancel')}
              </Button>
              <Button
                style={{
                  color: isDeleteHasHover ? 'red' : 'white',
                  background: !isDeleteHasHover ? 'red' : 'white',
                }}
                onMouseEnter={() => {
                  setIsDeleteHasHover(true);
                }}
                onMouseLeave={() => {
                  setIsDeleteHasHover(false);
                }}
                variant="error" onClick={confirmDelete}>
                {t('products.deleteProduct')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      {showDetailsModal && selectedProduct && (
        <ProductDetailsModal
          product={selectedProduct}
          onClose={closeDetailsModal}
          onEdit={(product) => {
            closeDetailsModal();
            handleEditProduct(product);
          }}
          t={t}
        />
      )}
    </div>
  );
};

export default Products;